import * as React from 'react';
import {
  Activity,
  Bot,
  AlertTriangle,
  Bell,
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { LiveIndicator } from './live-indicator';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/number-format';

export type FeedItemType = 'trade' | 'system' | 'alert' | 'action_required';
export type FeedItemSeverity = 'info' | 'warning' | 'error' | 'success';
export type ActionStatus = 'pending' | 'approved' | 'rejected' | 'expired';

export interface LiveFeedItem {
  id: string;
  type: FeedItemType;
  severity?: FeedItemSeverity;
  title: string;
  details?: string;
  botName?: string;
  exchange?: string;
  pnl?: number;
  timestamp: string;
  // Action required fields
  actionRequired?: boolean;
  actionStatus?: ActionStatus;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}

export interface LiveFeedSummary {
  tradeCount: number;
  totalPnl: number;
  winRate?: number;
  pendingActions: number;
}

export interface LiveFeedProps {
  items: LiveFeedItem[];
  summary?: LiveFeedSummary;
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onFilterChange?: (filter: FeedItemType | 'all') => void;
  activeFilter?: FeedItemType | 'all';
  locale?: string;
  maxHeight?: string;
  className?: string;
}

const filterOptions: { value: FeedItemType | 'all'; label: string; icon?: React.ReactNode }[] = [
  { value: 'all', label: 'All' },
  { value: 'action_required', label: 'Actions', icon: <Clock className="h-3 w-3" /> },
  { value: 'trade', label: 'Trades', icon: <Bot className="h-3 w-3" /> },
  { value: 'alert', label: 'Alerts', icon: <Bell className="h-3 w-3" /> },
  { value: 'system', label: 'System', icon: <Activity className="h-3 w-3" /> },
];

function formatRelativeTime(timestamp: string): string {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d ago`;
}

function getItemIcon(type: FeedItemType, severity?: FeedItemSeverity) {
  if (type === 'action_required') {
    return <Clock className="h-4 w-4" />;
  }
  switch (type) {
    case 'trade':
      return <Bot className="h-4 w-4" />;
    case 'alert':
      return <Bell className="h-4 w-4" />;
    case 'system':
      return severity === 'error' ? <AlertTriangle className="h-4 w-4" /> : <Activity className="h-4 w-4" />;
    default:
      return <Activity className="h-4 w-4" />;
  }
}

function getItemIconColor(type: FeedItemType, severity?: FeedItemSeverity) {
  if (type === 'action_required') return 'text-amber-500';
  if (severity === 'error') return 'text-rose-500';
  if (severity === 'warning') return 'text-amber-500';
  if (severity === 'success') return 'text-emerald-500';
  switch (type) {
    case 'trade':
      return 'text-blue-500';
    case 'alert':
      return 'text-amber-500';
    case 'system':
      return 'text-slate-500';
    default:
      return 'text-slate-500';
  }
}

function getActionStatusBadge(status?: ActionStatus) {
  if (!status) return null;
  const config = {
    pending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Pending' },
    approved: { icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
    rejected: { icon: XCircle, color: 'bg-rose-100 text-rose-700', label: 'Rejected' },
    expired: { icon: Clock, color: 'bg-slate-100 text-slate-500', label: 'Expired' },
  };
  const { icon: Icon, color, label } = config[status];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', color)}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

export function LiveFeed({
  items,
  summary,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onFilterChange,
  activeFilter = 'all',
  locale = 'de-CH',
  maxHeight = '500px',
  className,
}: LiveFeedProps) {
  const filteredItems = React.useMemo(() => {
    if (activeFilter === 'all') return items;
    if (activeFilter === 'action_required') {
      return items.filter((item) => item.actionRequired && item.actionStatus === 'pending');
    }
    return items.filter((item) => item.type === activeFilter);
  }, [items, activeFilter]);

  const filterCounts = React.useMemo(() => {
    return {
      all: items.length,
      action_required: items.filter((i) => i.actionRequired && i.actionStatus === 'pending').length,
      trade: items.filter((i) => i.type === 'trade').length,
      system: items.filter((i) => i.type === 'system').length,
      alert: items.filter((i) => i.type === 'alert').length,
    };
  }, [items]);

  const pendingActionsCount = filterCounts.action_required;

  return (
    <Card className={cn('border border-slate-200 flex flex-col', className)}>
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Activity className="h-4 w-4" aria-hidden="true" />
            Live Feed
            {pendingActionsCount > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">
                {pendingActionsCount}
              </span>
            )}
          </CardTitle>
          <LiveIndicator status="connected" className="text-xs" />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 min-h-0 space-y-3">
        {/* Filter Tabs */}
        {onFilterChange && (
          <div className="flex flex-wrap gap-1.5 shrink-0">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onFilterChange(option.value)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  activeFilter === option.value
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                )}
              >
                {option.icon}
                {option.label}
                {filterCounts[option.value] > 0 && (
                  <span
                    className={cn(
                      'ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold',
                      activeFilter === option.value ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600',
                      option.value === 'action_required' &&
                        filterCounts.action_required > 0 &&
                        activeFilter !== option.value &&
                        'bg-amber-200 text-amber-700'
                    )}
                  >
                    {filterCounts[option.value]}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Summary Bar */}
        {summary && (activeFilter === 'all' || activeFilter === 'trade') && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 shrink-0">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="font-medium text-slate-600">
                Today: <span className="text-slate-900">{summary.tradeCount} trades</span>
              </span>
              <span className="text-slate-300" aria-hidden="true">
                ·
              </span>
              <span
                className={cn(
                  'font-semibold tabular-nums',
                  summary.totalPnl > 0 ? 'text-emerald-600' : summary.totalPnl < 0 ? 'text-rose-600' : 'text-slate-600'
                )}
              >
                {formatCurrency(summary.totalPnl, { locale, decimals: 0, sign: 'always' })}
              </span>
              {summary.winRate !== undefined && (
                <>
                  <span className="text-slate-300" aria-hidden="true">
                    ·
                  </span>
                  <span className="text-slate-600">{Math.round(summary.winRate)}% win rate</span>
                </>
              )}
              {summary.pendingActions > 0 && (
                <>
                  <span className="text-slate-300" aria-hidden="true">
                    ·
                  </span>
                  <span className="text-amber-600 font-medium">{summary.pendingActions} actions pending</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Feed Items */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Activity className="mb-2 h-8 w-8 text-slate-300" aria-hidden="true" />
            <p className="text-sm text-slate-500">No activity</p>
            <p className="text-xs text-slate-400">
              {activeFilter === 'action_required'
                ? 'No pending actions require your attention'
                : 'Activity will appear here once orders are processed'}
            </p>
          </div>
        ) : (
          <div
            className="flex-1 overflow-y-auto space-y-2 pr-1"
            style={{ maxHeight }}
          >
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={cn(
                  'rounded-lg border bg-white p-3 shadow-sm transition-colors hover:bg-slate-50',
                  item.actionRequired && item.actionStatus === 'pending'
                    ? 'border-amber-200 bg-amber-50/30'
                    : 'border-slate-200'
                )}
              >
                <div className="flex items-start gap-3">
                  <span className={cn('mt-0.5 shrink-0', getItemIconColor(item.type, item.severity))}>
                    {getItemIcon(item.type, item.severity)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {item.botName && (
                          <span className="mb-0.5 inline-flex items-center gap-1 text-xs text-blue-600">
                            <Bot className="h-3 w-3" />
                            {item.botName}
                          </span>
                        )}
                        <p className="text-sm font-medium text-slate-900 truncate">{item.title}</p>
                        {item.details && <p className="mt-0.5 text-xs text-slate-500 truncate">{item.details}</p>}
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                          <span>{formatRelativeTime(item.timestamp)}</span>
                          {item.exchange && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span>{item.exchange}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {item.pnl !== undefined && (
                          <span
                            className={cn(
                              'text-xs font-semibold tabular-nums',
                              item.pnl > 0 ? 'text-emerald-600' : item.pnl < 0 ? 'text-rose-600' : 'text-slate-500'
                            )}
                          >
                            {formatCurrency(item.pnl, { locale, decimals: 2, sign: 'always' })}
                          </span>
                        )}
                        {item.actionRequired && getActionStatusBadge(item.actionStatus)}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {item.actionRequired && item.actionStatus === 'pending' && (
                      <div className="mt-2 flex items-center gap-2">
                        {item.onAction && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={item.onAction}
                            className="h-7 text-xs"
                          >
                            {item.actionLabel || 'Review'}
                            <ArrowRight className="ml-1 h-3 w-3" />
                          </Button>
                        )}
                        {item.onDismiss && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={item.onDismiss}
                            className="h-7 text-xs text-slate-500"
                          >
                            Dismiss
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        {hasMore && onLoadMore && (
          <div className="pt-2 text-center shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="text-xs text-slate-600 hover:text-slate-900"
            >
              {loadingMore ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <ChevronDown className="mr-1.5 h-3 w-3" />
              )}
              Load more
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
